// src/pages/Upgrades.jsx
import { Fragment, useEffect, useState, useRef } from "react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { ShieldAlert, X, UserSearch, Target, Send, Sparkles, Cpu, Network, Fingerprint, Share2, Mail, Newspaper, Zap, Key, Building, Globe, Database, MessageSquare, Terminal, ShieldCheck, Lock, Check, Gavel, Scale, ClipboardList, Crown, HelpCircle, CreditCard, Calendar, Hash, Eye } from "lucide-react";
import { redirectToLogin } from "../utils/auth";
import CheckoutModal from "../components/CheckoutModa"; // fixed typo
import { refreshEntitlements } from "../utils/brandProtection";

const CONTACT_API = "https://whiteintel.io/api/regular/app/v2/contact/contact.php";
const PAYMENT_LINKS_API = "https://whiteintel.io/api/regular/app/v2/user/get_payment_links.php";
const SUBSCRIPTION_API = "https://whiteintel.io/api/regular/app/v2/user/get_subscription.php";

// Map the backend `subscription` value to one of our matrix plan ids.
// Returns null when the user is on a plan that isn't in the matrix
// (e.g. trial / free), so we just don't show a "Your plan" chip.
//
// Known backend codes (from /v2/user/get_subscription.php):
//   "bb-researcher" → researcher
//   "enterprise"    → enterprise
//   "tifirm"        → threat_intel
function normalizeSubscriptionToPlanId(raw) {
  const s = String(raw || "").toLowerCase().trim();
  if (!s) return null;
  // Explicit codes first.
  if (s === "tifirm") return "threat_intel";
  if (s === "tifirm-extended" || s === "extended-tifirm" || s === "extended-ti") return "extended_threat_intel";
  if (s === "enterprise") return "enterprise";
  if (s === "bb-researcher") return "researcher";
  // Fuzzy fallback for future / variant codes (e.g. "Threat Intelligence",
  // "researcher_v2", etc.) so a backend rename doesn't silently hide the chip.
  const stripped = s.replace(/[\s_-]+/g, "");
  if (stripped.includes("researcher")) return "researcher";
  // Extended must be tested BEFORE the plain threat-intel match so the prefix
  // doesn't get swallowed by the more general rule below.
  if (stripped.includes("extended") && stripped.includes("threat") && stripped.includes("intel")) {
    return "extended_threat_intel";
  }
  if (stripped.includes("threat") && stripped.includes("intel")) return "threat_intel";
  if (stripped.includes("enterprise")) return "enterprise";
  return null;
}

const frosted =
  "border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-md shadow-[0_0_10px_rgba(56,189,248,0.05)]";

export default function Upgrades() {
  // layout
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  // billing cycle state
  const [billingCycle, setBillingCycle] = useState("monthly"); // "monthly" | "yearly"

  // Mobile-only: which single plan is currently expanded in the stacked
  // view (the comparison matrix doesn't fit below `md`, so we let users
  // pick one plan and see its full feature list).
  const [mobilePlanId, setMobilePlanId] = useState("threat_intel");

  // checkout modal
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  // contact modal
  const [contactOpen, setContactOpen] = useState(false);
  const [forPlan, setForPlan] = useState(null); 

  // payment links fetched from backend
  const [planLinks, setPlanLinks] = useState(null);
  const [linksError, setLinksError] = useState(null);

  // User's current subscription (normalised to a matrix plan id, or null
  // if they're on a plan we don't list — trial / free / etc.).
  const [currentPlanId, setCurrentPlanId] = useState(null);

  // tabs
  const [activeTab, setActiveTab] = useState("licenses"); // "licenses" | "addons"

  // URL <-> tab sync
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    if (t === "licenses" || t === "addons") setActiveTab(t);
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (activeTab === "addons") params.set("tab", "addons");
    else params.delete("tab");
    const qs = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash
    );
  }, [activeTab]);

  const openModal = (plan) => {
    setForPlan(plan);
    setContactOpen(true);
  };

  function getJwtToken() {
    try {
      return (
        localStorage.getItem("jwtToken") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("jwtToken") ||
        window.WHITEINTEL_TOKEN ||
        ""
      );
    } catch {
      return "";
    }
  }

  // fetch payment links
  useEffect(() => {
    let ignore = false;
    async function loadLinks() {
      setLinksError(null);
      try {
        const res = await fetch(PAYMENT_LINKS_API, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
        });

        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          return;
        }

        const json = await res.json().catch(() => ({}));

        if (!ignore && json?.success && json?.allowed && json?.links) {
          setPlanLinks(json.links);
        } else {
          setPlanLinks(null); 
        }
      } catch (e) {
        if (!ignore) {
          setPlanLinks(null);
          setLinksError("Unable to load payment links right now.");
        }
      }
    }
    loadLinks();
    return () => {
      ignore = true;
    };
  }, []);

  // Fetch the user's current subscription so we can mark "Your plan" on the
  // matching column and disable that column's CTA.
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetch(SUBSCRIPTION_API, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
        });
        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          return;
        }
        const json = await res.json().catch(() => ({}));
        if (!ignore && json?.success) {
          setCurrentPlanId(normalizeSubscriptionToPlanId(json.subscription));
        }
      } catch {
        // Silent — no "Your plan" chip is the right fallback when this fails.
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  const threatIntelLinks = planLinks?.threat_intel;
  const threatFeedAddonUrl = threatIntelLinks?.addons?.threat_feed_api_yearly || null;

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      {/* Header spacer */}
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-[20px] font-bold leading-tight tracking-tight">Upgrades & Licensing</h1>
          </div>
        </div>
      </header>

      {/* Sub-nav — outside the header */}
      <div
        className="border-b border-white/10 bg-[#000414]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-4 md:px-6 flex items-center gap-4 md:gap-5">
          <button
            onClick={() => setActiveTab("licenses")}
            className={`py-3 md:py-2 px-1 text-sm font-medium transition whitespace-nowrap -mb-px border-b-[3px] ${
              activeTab === "licenses"
                ? "text-white border-neutral-200"
                : "text-neutral-400 hover:text-white border-transparent"
            }`}
          >
            Licenses
          </button>
          <span aria-hidden className="hidden md:block h-3.5 w-px bg-neutral-700/60 -mb-px" />
          <button
            onClick={() => setActiveTab("addons")}
            className={`py-3 md:py-2 px-1 text-sm font-medium transition whitespace-nowrap -mb-px border-b-[3px] ${
              activeTab === "addons"
                ? "text-white border-neutral-200"
                : "text-neutral-400 hover:text-white border-transparent"
            }`}
          >
            Add-ons
          </button>
        </div>
      </div>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="upgrades"
          onChange={() => {}}
        />

        <main className="flex-1 px-4 md:px-6 pb-12">
          
          {/* Billing Cycle Toggle (Only show on Licenses tab) */}
          {activeTab === "licenses" && (
             <div className="flex justify-center mb-8 mt-4">
              <div className="bg-[#0a1224] p-1 rounded-md border border-sky-900/30 inline-flex relative">
                <button
                  onClick={() => setBillingCycle("monthly")}
                  className={`px-5 py-1.5 rounded text-[12px] font-medium transition-colors ${
                    billingCycle === "monthly"
                    ? "bg-sky-500/25 text-white border border-sky-500/40"
                    : "text-neutral-300 hover:text-white border border-transparent"
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle("yearly")}
                  className={`relative px-5 py-1.5 rounded text-[12px] font-medium transition-colors ${
                    billingCycle === "yearly"
                    ? "bg-sky-500/25 text-white border border-sky-500/40"
                    : "text-neutral-300 hover:text-white border border-transparent"
                  }`}
                >
                  Yearly
                  {/* "Save ~20%" badge — subtle ring chip, no glow. */}
                  <span className={`absolute -top-2.5 -right-7 px-1.5 py-0.5 rounded text-[9.5px] font-medium uppercase tracking-wider transition-colors whitespace-nowrap ${
                    billingCycle === "yearly"
                    ? "bg-sky-500/15 text-sky-200 ring-1 ring-inset ring-sky-500/40"
                    : "bg-sky-900/30 text-sky-300/80 ring-1 ring-inset ring-sky-500/20"
                  }`}>
                    Save ~17%
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Licenses — enterprise comparison matrix (RF / Datadog style).
              Plan columns at top with price + Subscribe; categorized feature
              rows underneath. Plan IDs: researcher / enterprise / threat_intel
              / extended_threat_intel — every CATEGORIES row needs a cell for
              each. New tiers go in PLAN_COLUMNS + normalizeSubscriptionToPlanId. */}
          {activeTab === "licenses" && (() => {
            const PLAN_COLUMNS = [
              {
                id: "researcher",
                name: "Researcher",
                tagline: "For solo researchers & freelancers",
                icon: Zap,
                priceMonthly: "$200",
                priceYearly: "$2,000 / year",
                yearlySavings: "$400",  // $200 × 12 = $2,400 vs $2,000 yearly
                yearlyOnly: false,
                links: planLinks?.researcher,
                contactName: "Researcher",
                highlight: false,
              },
              {
                id: "enterprise",
                name: "Enterprise",
                tagline: "For SOC teams of 2–5",
                icon: ShieldCheck,
                priceMonthly: "$400",
                priceYearly: "$4,000 / year",
                yearlySavings: "$800",  // $400 × 12 = $4,800 vs $4,000 yearly
                yearlyOnly: false,
                links: planLinks?.enterprise,
                contactName: "Enterprise",
                highlight: false,
              },
              {
                id: "threat_intel",
                name: "Threat Intelligence",
                shortName: "Threat Intel",
                tagline: "For MSSPs & Security products and enterprise SOCs",
                icon: Lock,
                priceMonthly: "$750",
                priceYearly: "$7,500 / year",
                yearlySavings: "$1,500",  // $750 × 12 = $9,000 vs $7,500 yearly
                yearlyOnly: false,
                links: threatIntelLinks,
                contactName: "Threat Intelligence",
                highlight: false,
              },
              {
                id: "extended_threat_intel",
                name: "Extended Threat Intelligence",
                shortName: "Extended TI",
                tagline: "All features and add-ons included",
                icon: Crown,
                priceMonthly: "$2,083",   // $25,000 / 12 ≈ $2,083 (display equivalent — yearlyOnly, no monthly billing)
                priceYearly: "$25,000 / year",
                yearlySavings: "",        // Not applicable: billed annually only.
                yearlyOnly: true,
                links: null,
                contactName: "Extended Threat Intelligence",
                highlight: true,
              },
            ];

            // Cell value vocabulary: "check" → ✓ icon; "dash" or null → —;
            // strings render verbatim. "Add-on" is a string but we tag it
            // visually with a chip via renderCell below. "check:<label>"
            // renders the tick alongside an annotation (e.g. quota copy).
            const CATEGORIES = [
              {
                name: "Core Features",
                rows: [
                  { label: "Fully unmasked data (incl. passwords)", cells: { researcher: "check", enterprise: "check", threat_intel: "check", extended_threat_intel: "check" } },
                  {
                    label: "Leak Discovery",
                    tooltip: "All identifiers: consumer accounts, corporate accounts, email addresses, attack surface, IP addresses, and computer names.",
                    cells: {
                      researcher: "Consumer, corporate, email, attack surface",
                      enterprise: "Consumer, corporate, email, attack surface",
                      threat_intel: "All identifiers available",
                      extended_threat_intel: "All identifiers available",
                    },
                  },
                  { label: "Data unlocks", cells: { researcher: "750 / month", enterprise: "Unlimited", threat_intel: "Unlimited", extended_threat_intel: "Unlimited" } },
                  {
                    label: "Threat Feeds",
                    tooltip: "Curated dark-web telemetry: data sales, ransomware lifecycle events, access listings, and adversary chatter.",
                    cells: { researcher: "GUI only", enterprise: "GUI only", threat_intel: "GUI only", extended_threat_intel: "GUI + API" },
                  },
                  {
                    label: "Brand Protection",
                    tooltip: "Look-alike domain tracking, brand impersonation detection, and abuse takedown workflows.",
                    cells: { researcher: "dash", enterprise: "check", threat_intel: "check", extended_threat_intel: "check" },
                  },
                  {
                    label: "Payment Fraud",
                    tooltip: "Compromised-card monitoring — BIN coverage, issuer attribution, validity tracking.",
                    cells: { researcher: "dash", enterprise: "Add-on", threat_intel: "Add-on", extended_threat_intel: "check:500 searches / day" },
                  },
                  {
                    label: "Investigation+",
                    tooltip: "Advanced pivoting, multi-identifier graph navigation, and analyst workflow tooling for threat-hunting teams.",
                    cells: { researcher: "Add-on", enterprise: "Add-on", threat_intel: "check", extended_threat_intel: "check" },
                  },
                  {
                    label: "Supplier Security",
                    tooltip: "Continuous credential-exposure scoring for your vendors and partners, with per-supplier risk grades and event timelines.",
                    cells: { researcher: "dash", enterprise: "64 suppliers", threat_intel: "128 suppliers", extended_threat_intel: "256 suppliers" },
                  },
                  { label: "User seats", cells: { researcher: "1", enterprise: "2", threat_intel: "3", extended_threat_intel: "5" } },
                ],
              },
              {
                name: "Exports & Downloads",
                rows: [
                  { label: "Data exports", cells: { researcher: "10 / month · 2-month look-back", enterprise: "10 / day · 1-year look-back", threat_intel: "500 / day · 2-year look-back", extended_threat_intel: "500 / day · 2-year look-back" } },
                  { label: "Full stealer-log archive downloads", cells: { researcher: "dash", enterprise: "dash", threat_intel: "check", extended_threat_intel: "check" } },
                ],
              },
              {
                name: "Monitoring",
                rows: [
                  { label: "Watchlist tokens", cells: { researcher: "10 domains + 5 emails", enterprise: "64 (domains + emails)", threat_intel: "128 (all identifiers)", extended_threat_intel: "256 (all identifiers)" } },
                  { label: "Dark-web mention monitoring", cells: { researcher: "dash", enterprise: "8 keywords", threat_intel: "16 keywords", extended_threat_intel: "64 keywords" } },
                  { label: "Public GitHub repository scanning", cells: { researcher: "dash", enterprise: "8 repositories", threat_intel: "16 repositories", extended_threat_intel: "64 repositories" } },
                  { label: "Look-alike domain monitoring", cells: { researcher: "dash", enterprise: "check", threat_intel: "check", extended_threat_intel: "check" } },
                  { label: "Direct content access for watchlist events", cells: { researcher: "dash", enterprise: "check", threat_intel: "check", extended_threat_intel: "check" } },
                ],
              },
              {
                name: "API Access",
                rows: [
                  { label: "Leak Discovery API", cells: { researcher: "dash", enterprise: "50 calls / day", threat_intel: "500 calls / day", extended_threat_intel: "1000 calls / day" } },
                  {
                    label: "Leak Discovery API endpoints",
                    tooltip: "Basic: consumer, corporate, email, and last leaks search endpoints. Full: adds IP-address and computer-name search endpoints.",
                    cells: { researcher: "dash", enterprise: "Basic", threat_intel: "Full", extended_threat_intel: "Full" },
                  },
                  { label: "Webhook integration & data streaming", cells: { researcher: "dash", enterprise: "dash", threat_intel: "check", extended_threat_intel: "check" } },
                  { label: "Threat Feed API", cells: { researcher: "dash", enterprise: "Add-on", threat_intel: "Add-on", extended_threat_intel: "1000 calls / day" } },
                  { label: "Payment Fraud API", cells: { researcher: "dash", enterprise: "Add-on", threat_intel: "Add-on", extended_threat_intel: "100 calls / day" } },
                ],
              },
              {
                name: "Alerting & Integrations",
                rows: [
                  { label: "Real-time alerts", cells: { researcher: "Metadata only", enterprise: "Full · CSV included", threat_intel: "Full · CSV included", extended_threat_intel: "Full · CSV included" } },
                  { label: "SSO", cells: { researcher: "dash", enterprise: "check", threat_intel: "check", extended_threat_intel: "check" } },
                  { label: "Jira", cells: { researcher: "dash", enterprise: "check", threat_intel: "check", extended_threat_intel: "check" } },
                  { label: "Slack", cells: { researcher: "dash", enterprise: "check", threat_intel: "check", extended_threat_intel: "check" } },
                  { label: "Investigation+ Module", cells: { researcher: "dash", enterprise: "Add-on", threat_intel: "check", extended_threat_intel: "check" } },
                  { label: "Managed Takedowns", cells: { researcher: "dash", enterprise: "Add-on", threat_intel: "Add-on", extended_threat_intel: "20 / year" } },
                ],
              },
              {
                name: "Commercial & Support",
                rows: [
                  { label: "Resale & customer-facing product rights", cells: { researcher: "dash", enterprise: "dash", threat_intel: "check", extended_threat_intel: "check" } },
                  { label: "Support tier", cells: { researcher: "Standard", enterprise: "Priority", threat_intel: "Priority developer", extended_threat_intel: "Priority developer" } },
                ],
              },
            ];

            const renderCell = (v) => {
              if (v === "check") {
                return (
                  <span
                    role="img"
                    aria-label="Included"
                    className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-inset ring-emerald-500/30"
                  >
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                );
              }
              // "check:<label>" renders the green tick + an annotation. Useful
              // when a feature is included AND has a quota worth surfacing,
              // e.g. "check:500 searches / day".
              if (typeof v === "string" && v.startsWith("check:")) {
                const label = v.slice("check:".length).trim();
                return (
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      role="img"
                      aria-label="Included"
                      className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-500/15 text-emerald-300 ring-1 ring-inset ring-emerald-500/30 shrink-0"
                    >
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    <span className="text-neutral-200 leading-snug">{label}</span>
                  </span>
                );
              }
              if (v === "dash" || v == null) {
                return (
                  <span
                    role="img"
                    aria-label="Not included"
                    className="inline-block h-5 w-5 rounded-full ring-1 ring-inset ring-neutral-600"
                  />
                );
              }
              if (v === "Add-on") {
                return (
                  <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded ring-1 ring-inset ring-amber-500/30 bg-amber-500/10 text-amber-200">
                    <Sparkles className="h-2.5 w-2.5" />
                    Add-on
                  </span>
                );
              }
              return <span className="text-neutral-200 leading-snug">{v}</span>;
            };

            const selectedMobilePlan =
              PLAN_COLUMNS.find((pp) => pp.id === mobilePlanId) || PLAN_COLUMNS[0];

            return (
              <>
              {/* Mobile: chip picker + single-plan stacked view (the matrix is
                  unusable below `md` — 4 columns swipe-horizontally on phones
                  and break comparison context). Fonts unified: labels 13px,
                  descriptions 12px, eyebrows 11px. */}
              <div className="md:hidden space-y-4 max-w-7xl">
                <div className="flex flex-wrap gap-2">
                  {PLAN_COLUMNS.map((pp) => {
                    const isPickedCurrent = currentPlanId === pp.id;
                    const chipLabel = pp.shortName || pp.name;
                    return (
                      <button
                        key={pp.id}
                        onClick={() => setMobilePlanId(pp.id)}
                        className={`flex-1 basis-[calc(50%-4px)] sm:basis-0 inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-[12.5px] font-medium border transition-colors min-w-0 ${
                          mobilePlanId === pp.id
                            ? "bg-sky-500/20 border-sky-500/40 text-sky-100"
                            : "bg-transparent border-sky-900/40 text-neutral-300 hover:text-white"
                        }`}
                      >
                        <span className="truncate">{chipLabel}</span>
                        {isPickedCurrent && (
                          <span
                            aria-label="Your plan"
                            title="Your plan"
                            className="h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>

                {(() => {
                  const p = selectedMobilePlan;
                  const displayCycle = billingCycle;
                  const paymentCycle = p.yearlyOnly ? "yearly" : billingCycle;
                  const activePrice =
                    displayCycle === "monthly"
                      ? p.priceMonthly
                      : (p.priceYearly || "").split(" /")[0];
                  const activePeriod = displayCycle === "monthly" ? "/ mo" : "/ yr";
                  const activeLink =
                    paymentCycle === "monthly" ? p.links?.monthly : p.links?.yearly;
                  const hasLinks = !!(p.links && (p.links.monthly || p.links.yearly));
                  const yearlyTotal = (p.priceYearly || "").split(" /")[0];
                  const isCurrent = currentPlanId === p.id;

                  return (
                    <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 overflow-hidden">
                      {/* Summary */}
                      <div className={`px-5 py-5 border-b border-sky-900/30 ${p.highlight ? "bg-sky-500/[0.04]" : ""}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-[16px] font-semibold text-neutral-100 tracking-tight">
                            {p.name}
                          </div>
                          {isCurrent && (
                            <span className="text-[9.5px] font-semibold uppercase tracking-wider text-emerald-300 ring-1 ring-inset ring-emerald-500/40 bg-emerald-500/10 rounded px-1.5 py-0.5">
                              Your plan
                            </span>
                          )}
                          {p.highlight && !isCurrent && (
                            <span className="text-[9.5px] font-semibold uppercase tracking-wider text-sky-300 ring-1 ring-inset ring-sky-500/40 bg-sky-500/10 rounded px-1.5 py-0.5">
                              Most capable
                            </span>
                          )}
                        </div>
                        {p.tagline && (
                          <div className="mt-1 text-[12px] text-neutral-500 leading-snug">
                            {p.tagline}
                          </div>
                        )}
                        <div className="mt-3 flex items-baseline gap-1.5">
                          <span className="text-[24px] font-semibold tabular-nums leading-none text-white">
                            {activePrice}
                          </span>
                          <span className="text-[10.5px] font-medium text-neutral-500 uppercase tracking-wider">
                            {activePeriod}
                          </span>
                        </div>
                        <div className="mt-1.5 text-[12px] text-neutral-500">
                          {p.yearlyOnly
                            ? displayCycle === "monthly"
                              ? "Per-month equivalent · Billed annually only"
                              : "Billed annually only"
                            : displayCycle === "yearly"
                            ? <>Billed annually · <span className="text-emerald-300">{p.yearlySavings} / yr saved</span></>
                            : "Cancel anytime"}
                        </div>
                        <button
                          onClick={() => {
                            if (isCurrent) return;
                            if (hasLinks && activeLink) setCheckoutUrl(activeLink);
                            else openModal(p.contactName);
                          }}
                          disabled={isCurrent}
                          className={`mt-4 w-full py-2.5 rounded-md font-semibold text-[13px] transition-colors ${
                            isCurrent
                              ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 cursor-default"
                              : p.highlight
                              ? "bg-sky-500 hover:bg-sky-400 text-white border border-sky-500"
                              : "bg-transparent border border-sky-700/60 hover:bg-white/[0.04] hover:border-sky-500/60 text-neutral-100"
                          }`}
                        >
                          {isCurrent
                            ? "Current plan"
                            : hasLinks && activeLink
                            ? `Subscribe ${paymentCycle === "yearly" ? "yearly" : "monthly"}`
                            : "Contact sales"}
                        </button>
                      </div>

                      {/* Feature list by category — label-left, value-right rows */}
                      <div>
                        {CATEGORIES.map((cat) => (
                          <div key={cat.name}>
                            <div className="px-5 pt-4 pb-2 bg-[#0b1426]/40 border-t border-sky-900/20">
                              <div className="text-[11px] font-semibold uppercase tracking-wider text-sky-300/80">
                                {cat.name}
                              </div>
                            </div>
                            {cat.rows.map((row, ri) => (
                              <div
                                key={ri}
                                className="flex items-start gap-3 px-5 py-2.5 border-t border-sky-900/15"
                              >
                                <div className="text-[13px] text-neutral-300 leading-snug flex-1 min-w-0">
                                  <div>{row.label}</div>
                                  {row.tooltip && (
                                    <div className="mt-0.5 text-[12px] text-neutral-500 leading-snug">
                                      {row.tooltip}
                                    </div>
                                  )}
                                </div>
                                <div className="text-[13px] text-neutral-200 leading-snug text-right max-w-[55%] break-words">
                                  {renderCell(row.cells[p.id])}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Desktop / tablet: full comparison matrix */}
              <div className="hidden md:block max-w-7xl overflow-x-auto rounded-md border border-sky-900/30 bg-[#0a1224]/70">
                <table className="w-full text-[12.5px] border-collapse">
                  {/* Sticky column-header row: plan summary + Subscribe CTA */}
                  <thead className="sticky top-0 z-10 bg-[#0a1224]/95 backdrop-blur">
                    <tr>
                      <th className="w-[26%] min-w-[200px] text-left align-bottom px-5 py-5 border-b border-sky-900/30">
                        <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-400">
                          Plan comparison
                        </div>
                        <div className="mt-1 text-[11px] text-neutral-500 normal-case tracking-normal font-normal">
                          Prices reflect the {billingCycle === "yearly" ? "annual" : "monthly"} cycle selected above.
                        </div>
                      </th>
                      {PLAN_COLUMNS.map((p) => {
                        const displayCycle = billingCycle;
                        const paymentCycle = p.yearlyOnly ? "yearly" : billingCycle;
                        const activePrice =
                          displayCycle === "monthly"
                            ? p.priceMonthly
                            : (p.priceYearly || "").split(" /")[0];
                        const activePeriod = displayCycle === "monthly" ? "/ mo" : "/ yr";
                        const activeLink =
                          paymentCycle === "monthly" ? p.links?.monthly : p.links?.yearly;
                        const hasLinks = !!(p.links && (p.links.monthly || p.links.yearly));
                        const yearlyTotal = (p.priceYearly || "").split(" /")[0];
                        const isCurrent = currentPlanId === p.id;

                        return (
                          <th
                            key={p.id}
                            className={`min-w-[200px] text-left align-bottom px-5 py-5 border-b border-b-sky-900/30 border-l border-l-sky-900/15 ${
                              p.highlight ? "bg-sky-500/[0.04]" : ""
                            }`}
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="text-[14px] font-semibold text-neutral-100 tracking-tight">
                                {p.name}
                              </div>
                              {isCurrent && (
                                <span className="text-[9.5px] font-semibold uppercase tracking-wider text-emerald-300 ring-1 ring-inset ring-emerald-500/40 bg-emerald-500/10 rounded px-1.5 py-0.5">
                                  Your plan
                                </span>
                              )}
                              {p.highlight && !isCurrent && (
                                <span className="text-[9.5px] font-semibold uppercase tracking-wider text-sky-300 ring-1 ring-inset ring-sky-500/40 bg-sky-500/10 rounded px-1.5 py-0.5">
                                  Most capable
                                </span>
                              )}
                            </div>
                            {/* Audience tagline — answers "who is this plan for?" */}
                            {p.tagline && (
                              <div className="mt-1 text-[11px] text-neutral-500 leading-snug">
                                {p.tagline}
                              </div>
                            )}
                            <div className="mt-3 flex items-baseline gap-1.5">
                              <span className="text-[22px] font-semibold tabular-nums leading-none text-white">
                                {activePrice}
                              </span>
                              <span className="text-[10.5px] font-medium text-neutral-500 uppercase tracking-wider">
                                {activePeriod}
                              </span>
                            </div>
                            <div className="mt-1.5 text-[10px] text-neutral-500">
                              {p.yearlyOnly
                                ? displayCycle === "monthly"
                                  ? "Per-month equivalent · Billed annually only"
                                  : "Billed annually only"
                                : displayCycle === "yearly"
                                ? <>Billed annually · <span className="text-emerald-300">{p.yearlySavings} / yr saved</span></>
                                : "Cancel anytime"}
                            </div>
                            <button
                              onClick={() => {
                                if (isCurrent) return;
                                if (hasLinks && activeLink) setCheckoutUrl(activeLink);
                                else openModal(p.contactName);
                              }}
                              disabled={isCurrent}
                              className={`mt-3 w-full py-2 rounded-md font-semibold text-[12.5px] transition-colors ${
                                isCurrent
                                  ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 cursor-default"
                                  : p.highlight
                                  ? "bg-sky-500 hover:bg-sky-400 text-white border border-sky-500"
                                  : "bg-transparent border border-sky-700/60 hover:bg-white/[0.04] hover:border-sky-500/60 text-neutral-100"
                              }`}
                            >
                              {isCurrent
                                ? "Current plan"
                                : hasLinks && activeLink
                                ? `Subscribe ${paymentCycle === "yearly" ? "yearly" : "monthly"}`
                                : "Contact sales"}
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>

                  {/* Categorized feature rows */}
                  <tbody>
                    {CATEGORIES.map((cat) => (
                      <Fragment key={cat.name}>
                        <tr>
                          <td
                            colSpan={PLAN_COLUMNS.length + 1}
                            className="px-5 pt-5 pb-2 border-t border-sky-900/20 bg-[#0b1426]/40"
                          >
                            <div className="text-[11px] font-semibold uppercase tracking-wider text-sky-300/80">
                              {cat.name}
                            </div>
                          </td>
                        </tr>
                        {cat.rows.map((row, ri) => (
                          <tr
                            key={`${cat.name}-${ri}`}
                            className="border-t border-sky-900/15 hover:bg-white/[0.015]"
                          >
                            <td className="px-5 py-3 text-neutral-300 align-top w-[26%] min-w-[200px]">
                              <div>{row.label}</div>
                              {row.tooltip && (
                                <div className="mt-0.5 text-[11px] text-neutral-500 leading-snug">
                                  {row.tooltip}
                                </div>
                              )}
                            </td>
                            {PLAN_COLUMNS.map((p) => (
                              <td
                                key={p.id}
                                className={`px-5 py-3 align-top border-l border-sky-900/15 ${
                                  p.highlight ? "bg-sky-500/[0.03]" : ""
                                }`}
                              >
                                {renderCell(row.cells[p.id])}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            );
          })()}

          {/* Add-ons — uniform card pattern, RF-calm aesthetic:
              one accent (sky), no glow, label/value detail rows, dot-bullet
              inclusion lists, no decorative warning boxes. */}
          {activeTab === "addons" && (() => {
            // RF / Mandiant-style datasheet model: outcome-oriented
            // use cases lead, then a dense data-points line, then a
            // structured access spec. Pricing and eligibility live
            // inside Access rather than being headline decoration —
            // matches how RF presents Intelligence Cards in their
            // catalog.
            const ADDONS = [
              {
                key: "payment_fraud",
                name: "Payment Fraud Intelligence",
                tagline: "Compromised-card intelligence for fraud teams.",
                useCases: [
                  "Validate cards against the exposed-card index before transaction approval.",
                  "Surface exposure by issuer or BIN range to prioritize chargeback prevention.",
                  "Build a watchlist of your own BINs for continuous monitoring.",
                ],
                dataPoints: [
                  "BIN-6 · BIN-8 · last 4",
                  "Issuer bank",
                  "Issuer country",
                  "Card network",
                  "Card type",
                  "Card tier",
                  "Expiry date",
                  "Validity computed at query time",
                ],
                access: [
                  { label: "UI",          value: "500 searches / day" },
                  { label: "API",         value: "100 calls / day" },
                  { label: "Watchlist",   value: "Up to 64 BINs" },
                  { label: "Billing",     value: "$17,500 USD / year — annual" },
                  { label: "Eligibility", value: "Enterprise · Threat Intelligence" },
                ],
                onContact: () => openModal("Payment Fraud Intelligence"),
              },
              {
                key: "threat_feed",
                name: "Threat Feed API",
                tagline: "Structured dark-web telemetry as a JSON stream.",
                useCases: [
                  "Enrich SIEM events with adversary, ransomware, and access-broker context.",
                  "Track ransomware lifecycle from initial chatter to public leak.",
                  "Detect emerging attack vectors and zero-day signals against your sector.",
                ],
                dataPoints: [
                  "Forum monitoring",
                  "Geographic attribution",
                  "Industry targeting",
                  "Attack-vector identification",
                  "Threat-actor attribution",
                  "Ransomware-event tracking",
                  "Data auctions",
                  "Initial-access brokerage",
                  "Zero-day & exploit signals",
                  "OSINT cross-references",
                ],
                access: [
                  { label: "API",         value: "50 calls / day" },
                  { label: "Billing",     value: "$3,000 USD / year — annual" },
                  { label: "Eligibility", value: "Enterprise · Threat Intelligence" },
                ],
                onBuy: threatFeedAddonUrl ? () => setCheckoutUrl(threatFeedAddonUrl) : null,
                onContact: () => openModal("Threat Feed API"),
              },
              {
                key: "investigations",
                name: "Investigation+",
                badge: "Beta",
                tagline: "Neural unmasking and forensic deep-trace tooling.",
                useCases: [
                  "Trace adversary identities across stolen credentials and reused tradecraft.",
                  "Profile victim exposure surface for incident response.",
                  "Discover pivot points across leak corpora to drive investigations.",
                ],
                dataPoints: [
                  "Adversary unmasking",
                  "Victimology profiles",
                  "Automated risk scoring",
                  "Target prioritization",
                  "Identity mapping",
                  "Lateral vector analysis",
                  "Forensic footprinting",
                  "Pivot-point discovery",
                ],
                access: [
                  { label: "Billing",     value: "$1,500 USD / year — annual" },
                  { label: "Eligibility", value: "Researcher · Enterprise" },
                ],
                onContact: () => openModal("Investigation+ Module"),
              },
              {
                key: "takedowns",
                name: "Managed Takedowns",
                tagline: "Analyst-driven registrar abuse program.",
                useCases: [
                  "Remove phishing and look-alike domains targeting your brand.",
                  "Coordinate takedowns across registrars with evidence bundling.",
                  "Track each case from filing through resolution.",
                ],
                dataPoints: [
                  "Look-alike domains",
                  "Phishing & credential harvesters",
                  "Registrar abuse filing",
                  "Evidence bundles",
                  "Provider escalations",
                  "Lifecycle tracking",
                  "Ad-hoc takedown requests",
                  "Analyst communication",
                ],
                access: [
                  { label: "Pack size",   value: "10 takedowns" },
                  { label: "Pricing",     value: "$1,000 USD / pack — $100 each" },
                  { label: "Validity",    value: "Never expires while subscribed" },
                  { label: "Eligibility", value: "Enterprise · Threat Intelligence" },
                ],
                note: "Credit refund — takedowns rejected by non-compliant registrars outside our reach are credited back; you only pay for actionable cases.",
                onContact: () => openModal("Managed Takedowns"),
              },
            ];
            return (
              <div className="max-w-7xl grid grid-cols-1 xl:grid-cols-2 gap-4">
                {ADDONS.map((a, i) => (
                  <AddonCard key={a.key} index={i + 1} {...a} />
                ))}
              </div>
            );
          })()}

          {/* Optional: error banner if links failed to load */}
          {linksError && (
            <div className="max-w-7xl mt-6">
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-amber-100">
                {linksError}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Checkout modal (single instance) */}
      <CheckoutModal
        open={!!checkoutUrl}
        checkoutUrl={checkoutUrl}
        onClose={() => {
          setCheckoutUrl(null);
          // The user may have just upgraded — re-check entitlements so the
          // sidebar locks/pills update without waiting for them to refresh.
          refreshEntitlements();
        }}
        title="WhiteIntel — Secure Checkout"
      />

      {/* Contact modal */}
      <ContactLicenseModal
        open={contactOpen}
        planName={forPlan}
        onClose={() => setContactOpen(false)}
      />
    </div>
  );
}

/* ---------------- UI Bits ---------------- */

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={[
        "px-3 py-2 text-sm rounded-md border transition",
        active
          ? "border-sky-500/40 bg-sky-500/10 text-sky-200"
          : "border-sky-900/30 bg-[#0a1224]/70 text-neutral-300 hover:bg-white/5",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

/* ---------------- Components ---------------- */

function PlanCard({
  name,
  icon: Icon,
  billingCycle,
  priceMonthly,
  priceYearly,
  features = [],
  availableAddons = [],
  onContact,
  links,
  onOpenCheckout,
  highlight = false,
  // When true, the card ignores the billing-cycle toggle and always shows
  // the yearly price + yearly checkout link. Used for plans only sold
  // annually (e.g. Extended Threat Intel).
  yearlyOnly = false,
}) {
  const hasLinks = !!(links && (links.monthly || links.yearly));

  // Yearly-only plans (e.g. Extended Threat Intel) show a monthly-equivalent
  // price when the toggle is on "monthly" so users can compare against the
  // other plans, but the billing reality + checkout link stay on yearly.
  const displayCycle = billingCycle;
  const paymentCycle = yearlyOnly ? "yearly" : billingCycle;

  const activePrice = displayCycle === "monthly" ? priceMonthly : priceYearly.split(' /')[0];
  const activePeriod = displayCycle === "monthly" ? "/ month" : "/ year";
  const yearlyTotal = (priceYearly || "").split(' /')[0];

  const activeLink = paymentCycle === "monthly" ? links?.monthly : links?.yearly;

  return (
    <div
      className={`relative flex flex-col p-7 rounded-md transition-colors ${
        highlight
          ? "bg-[#0a1224]/80 border border-sky-500/40"
          : "bg-[#0a1224]/70 border border-sky-900/30 hover:border-sky-700/40"
      }`}
    >
      {highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sky-500/15 border border-sky-500/40 text-sky-100 text-[10px] font-semibold uppercase tracking-wider px-3 py-1 rounded-md">
          Most Capable
        </div>
      )}

      <div className="flex items-center justify-between mb-5">
        <div className={`p-2 rounded-md ${highlight ? 'bg-sky-500/15 text-sky-300' : 'bg-white/[0.04] text-neutral-400'}`}>
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="text-[15px] font-semibold tracking-tight text-neutral-100">{name}</h3>
      </div>

      <div className="mb-6 border-b border-sky-900/30 pb-5">
        <div className="flex items-baseline gap-1.5">
          <span className="text-[32px] font-semibold tabular-nums leading-none text-white">
            {activePrice}
          </span>
          <span className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider">
            {activePeriod}
          </span>
        </div>
        
        <div className="mt-2 space-y-1">
          {yearlyOnly ? (
            <p className="text-[11px] font-medium text-sky-300 uppercase tracking-wider">
              {displayCycle === "monthly" ? "Per-month equivalent · Billed annually only" : "Billed annually only"}
            </p>
          ) : displayCycle === "yearly" ? (
            <p className="text-[11px] font-medium text-sky-300 uppercase tracking-wider">
              Billed annually
            </p>
          ) : (
            <p className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider">
              Cancel anytime
            </p>
          )}
          <p className="text-[10px] text-neutral-600 uppercase tracking-wider">
            + Applicable Taxes
          </p>
        </div>
      </div>

      <div className="flex-1 space-y-1 mb-6">
        {features.map((f, i) => {
           const text = typeof f === "string" ? f : f.text;
           const enabled = typeof f === "string" ? true : f.enabled;
           return (
             <div key={i} className="flex items-start py-1">
               {enabled ? (
                 <Check className="h-4 w-4 mt-1 text-sky-500 shrink-0" />
               ) : (
                 <X className="h-4 w-4 mt-1 text-neutral-700 shrink-0" />
               )}
               <span className={`ml-3 text-[13px] leading-tight ${enabled ? "text-neutral-300 font-medium" : "text-neutral-600 line-through decoration-neutral-700"}`}>
                 {text}
               </span>
             </div>
           );
        })}
      </div>

      {availableAddons.length > 0 && (
        <div className="pt-4 mb-6 border-t border-sky-900/30">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-400 mb-2">
            Available add-ons
          </div>
          <div className="flex flex-wrap gap-1.5">
            {availableAddons.map((a) => (
              <span
                key={a}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded ring-1 ring-inset ring-sky-500/20 bg-sky-500/10 text-sky-200"
              >
                <Sparkles className="h-3 w-3" />
                {a}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="pt-5 border-t border-sky-900/30 mt-auto">
        {hasLinks && activeLink ? (
           <button
             onClick={() => onOpenCheckout?.(activeLink)}
             className={`w-full py-3 rounded-md font-semibold text-sm transition-colors ${
               highlight
                ? "bg-sky-500/15 border border-sky-500/40 hover:bg-sky-500/25 text-sky-100"
                : "bg-white/5 border border-sky-900/40 hover:bg-white/10 text-neutral-100"
             }`}
           >
             Subscribe {paymentCycle === "yearly" ? "Yearly" : "Monthly"}
           </button>
        ) : (
          <button
            onClick={onContact}
            className="w-full py-3 rounded-md bg-white/5 border border-sky-900/40 text-neutral-200 hover:bg-white/10 font-medium text-sm transition-colors flex items-center justify-center gap-2"
          >
            <Send className="w-4 h-4" />
            Contact Sales Team
          </button>
        )}
      </div>
    </div>
  );
}


/**
 * Add-on datasheet — Recorded Future / Mandiant aesthetic.
 *
 * Anatomy (top to bottom):
 *   - Header: numbered eyebrow, product name + optional badge, single-
 *     line tagline. No icon tile, no marketing chrome.
 *   - "Use cases" — outcome-first sentences (what an analyst will DO).
 *   - "Data points" — dense dot-separated capabilities line so the spec
 *     reads as a paragraph, not a feature dump.
 *   - "Access" — spec-sheet label/value rows (UI quota, API quota,
 *     billing, eligibility…). Pricing lives here, not as headline
 *     decoration.
 *   - Optional fine-print note (refund policies, etc).
 *   - Footer: primary "Talk to sales →" with optional "Subscribe yearly"
 *     when a checkout link exists. Contact email anchored right.
 *
 * Props:
 *   index, name, badge?, tagline
 *   useCases: string[]
 *   dataPoints: string[]
 *   access: [{label, value}]
 *   note?
 *   onBuy?, onContact, contactEmail?
 */
function AddonCard({
  index,
  name,
  badge,
  tagline,
  useCases = [],
  dataPoints = [],
  access = [],
  note,
  onBuy,
  onContact,
  contactEmail = "info@whiteintel.io",
}) {
  const idx = String(index ?? "").padStart(2, "0");
  return (
    <article className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 hover:border-sky-700/50 transition-colors flex flex-col">
      {/* Header — product name + numbered position. Mirrors how RF
          stamps "01", "02", … on each Intelligence Card in their
          catalog so the list reads as a curated product line. */}
      <header className="px-6 pt-5 pb-4 border-b border-sky-900/20">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-[16px] font-semibold tracking-tight text-neutral-100">
                {name}
              </h3>
              {badge && (
                <span className="text-[9px] px-1.5 py-0.5 ring-1 ring-inset ring-sky-500/30 bg-sky-500/10 text-sky-300 rounded uppercase tracking-[0.12em] font-semibold">
                  {badge}
                </span>
              )}
            </div>
            {tagline && (
              <p className="mt-1 text-[12.5px] text-neutral-400 leading-snug">
                {tagline}
              </p>
            )}
          </div>
          {idx && (
            <span className="font-mono text-[10.5px] uppercase tracking-[0.2em] text-neutral-500 shrink-0 pt-1">
              {idx}
            </span>
          )}
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 flex flex-col gap-5 px-6 py-5">
        {/* Use cases — outcome-oriented, each sentence stands alone. */}
        {useCases.length > 0 && (
          <section>
            <h4 className="text-[10px] uppercase tracking-[0.12em] font-semibold text-neutral-500 mb-2.5">
              Use cases
            </h4>
            <ul className="space-y-1.5">
              {useCases.map((u, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-[12.5px] text-neutral-200 leading-relaxed"
                >
                  <span className="text-neutral-600 shrink-0 mt-0.5">→</span>
                  <span>{u}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Data points — dense paragraph-like enumeration, separated by
            middots. Avoids the SaaS-y "decorated bullet list". */}
        {dataPoints.length > 0 && (
          <section>
            <h4 className="text-[10px] uppercase tracking-[0.12em] font-semibold text-neutral-500 mb-2">
              Data points
            </h4>
            <p className="text-[12.5px] text-neutral-300 leading-relaxed">
              {dataPoints.map((d, i) => (
                <span key={i}>
                  {i > 0 && <span className="text-neutral-600"> · </span>}
                  {d}
                </span>
              ))}
            </p>
          </section>
        )}

        {/* Access — spec sheet style label/value rows. Billing and
            eligibility sit alongside quotas as just another spec. */}
        {access.length > 0 && (
          <section>
            <h4 className="text-[10px] uppercase tracking-[0.12em] font-semibold text-neutral-500 mb-2.5">
              Access
            </h4>
            <dl className="divide-y divide-sky-900/15 border-y border-sky-900/15">
              {access.map((a, i) => (
                <div
                  key={i}
                  className="grid grid-cols-[110px_1fr] gap-3 items-baseline py-2"
                >
                  <dt className="text-[11px] uppercase tracking-[0.08em] text-neutral-500">
                    {a.label}
                  </dt>
                  <dd className="text-[12.5px] text-neutral-200 break-words">
                    {a.value}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {/* Note */}
        {note && (
          <p className="text-[11.5px] text-neutral-400 leading-relaxed">
            {note}
          </p>
        )}
      </div>

      {/* Footer — Talk to sales primary. "Subscribe yearly" surfaces
          only when a real checkout link exists (Threat Feed today). */}
      <footer className="px-6 py-4 border-t border-sky-900/20 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={onContact}
          className="inline-flex items-center gap-1.5 rounded-md border border-sky-700/60 bg-transparent hover:bg-sky-500/10 hover:border-sky-500/60 text-neutral-100 px-3 py-1.5 text-[12.5px] font-medium transition-colors"
        >
          Talk to sales
          <span aria-hidden>→</span>
        </button>
        {onBuy && (
          <button
            type="button"
            onClick={onBuy}
            className="inline-flex items-center gap-1.5 rounded-md bg-sky-500 hover:bg-sky-400 border border-sky-500 text-white px-3 py-1.5 text-[12.5px] font-semibold transition-colors"
          >
            Subscribe yearly
          </button>
        )}
        {contactEmail && (
          <a
            href={`mailto:${contactEmail}`}
            className="ml-auto text-[11px] font-mono text-neutral-500 hover:text-sky-400 transition-colors"
          >
            {contactEmail}
          </a>
        )}
      </footer>
    </article>
  );
}


function ContactLicenseModal({ open, onClose, planName }) {
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(null);
  const [err, setErr] = useState(null);
  const dialogRef = useRef(null);

  const [subscriptionType, setSubscriptionType] = useState("");

  const isThreatFeedAddon =
    typeof planName === "string" &&
    planName.toLowerCase().includes("threat feed api");

  const isInvestigations =
    typeof planName === "string" &&
    planName.toLowerCase().includes("investigation");

  const isManagedTakedowns =
    typeof planName === "string" &&
    planName.toLowerCase().includes("takedown");

  const isPaymentFraud =
    typeof planName === "string" &&
    planName.toLowerCase().includes("payment fraud");

  const isAddon = isThreatFeedAddon || isInvestigations || isManagedTakedowns || isPaymentFraud;

  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement;
    dialogRef.current?.focus();
    return () => prev && prev.focus && prev.focus();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setName("");
      setMsg("");
      setLoading(false);
      setOk(null);
      setErr(null);
      setSubscriptionType("");
    }
  }, [open]);

  if (!open) return null;

  function getJwtToken() {
    try {
      return (
        localStorage.getItem("jwtToken") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("jwtToken") ||
        window.WHITEINTEL_TOKEN ||
        ""
      );
    } catch {
      return "";
    }
  }

  async function handleSend() {
    setOk(null);
    setErr(null);
    if (!msg.trim()) {
      setErr("Please enter a message.");
      return;
    }

    const category = isAddon ? "addon_inquiry" : "subscription_upgrade";
    const title = isAddon
      ? `${planName || "General"} Inquiry`
      : `${planName || "General"} License Inquiry (${
          subscriptionType === "yearly" ? "Yearly" : "Monthly"
        })`;

    const payload = {
      category,
      title,
      message: name.trim() ? `From: ${name}\n\n${msg}` : msg,
    };

    try {
      const res = await fetch(CONTACT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify(payload),
      });

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }
      if (!res.ok) {
        let errMsg = `HTTP ${res.status}`;
        try {
          const errJson = await res.json();
          if (errJson?.error) errMsg = errJson.error;
        } catch {}
        throw new Error(errMsg);
      }

      const json = await res.json();
      if (json && (json.success === true || json.status === "ok")) {
        setOk("Thanks! Your message has been sent. We’ll get back to you shortly.");
      } else {
        setOk(
          "You will receive an email from us shortly. If it does not appear in your inbox, please check your spam or junk folder."
        );
      }
      setName("");
      setMsg("");
    } catch (e) {
      setErr(e.message || "Failed to send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[420]" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Panel */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="absolute right-0 top-0 h-full w-full max-w-2xl bg-[#000414] border-l border-neutral-800 shadow-[0_0_20px_rgba(56,189,248,0.08)] outline-none flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-800">
          <div>
            <div className="text-sm text-neutral-400">{planName ? `Contact — ${planName}` : "Contact"}</div>
            <div className="text-xl font-semibold">
              Contact Us{planName ? ` for ${planName}` : ""}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-white/5" title="Close">
            <X className="h-5 w-5 text-neutral-300" />
          </button>
        </div>

        {/* Banners */}
        {err && (
          <div className="px-5 pt-4">
            <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200 flex items-start justify-between">
              <span className="pr-4">{err}</span>
              <button onClick={() => setErr(null)} className="ml-3 text-xs text-neutral-400 hover:text-white" type="button">
                Dismiss
              </button>
            </div>
          </div>
        )}
        {ok && (
          <div className="px-5 pt-4">
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200 flex items-start justify-between">
              <span className="pr-4">{ok}</span>
              <button onClick={() => setOk(null)} className="ml-3 text-xs text-neutral-400 hover:text-white" type="button">
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="p-5 space-y-6 overflow-y-auto flex-1">
          <div className={`rounded-md ${frosted} p-4 space-y-4`}>
            <div className="grid grid-cols-1 gap-4">
              {/* Name */}
              <div>
                <div className="text-[11px] uppercase tracking-wide text-neutral-400 mb-2">Your Name</div>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Jane Doe"
                  className="w-full h-11 rounded-md border border-sky-900/30 bg-[#0a1224]/80 px-3 text-sm text-white outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40"
                />
              </div>

              {/* Subscription Type — HIDDEN for Addons */}
              {!isAddon && (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-neutral-400 mb-2">Subscription Type</div>
                  <select
                    value={subscriptionType}
                    onChange={(e) => setSubscriptionType(e.target.value)}
                    className="w-full h-11 rounded-md border border-sky-900/30 bg-[#0a1224]/80 px-3 text-sm text-white outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40"
                  >
                    <option value="">Select subscription type</option>
                    <option value="monthly">Monthly Subscription</option>
                    <option value="yearly">Yearly Subscription</option>
                  </select>
                </div>
              )}

              {/* Message */}
              <div>
                <div className="text-[11px] uppercase tracking-wide text-neutral-400 mb-2">Message</div>
                <textarea
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  placeholder={
                    isThreatFeedAddon
                      ? "Tell us about your needs for the Threat Feed API…"
                      : `Tell us about your needs for ${planName || "the selected option"}…`
                  }
                  rows={6}
                  className="w-full rounded-md border border-sky-900/30 bg-[#0a1224]/80 p-3 text-sm text-white outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40"
                />
              </div>
            </div>

            {/* KYC Banner — HIDDEN for Addons */}
            {!isAddon && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-4 text-amber-100">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="h-5 w-5 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold">KYC & Due Diligence</div>
                    <p className="mt-1 text-sm opacity-90">
                      Because our threat intelligence may include sensitive or confidential information, we conduct Know Your
                      Customer (KYC) checks before enabling full access. To accelerate verification, please include as much context
                      as possible:
                    </p>
                    <ul className="mt-2 text-sm list-disc pl-5 space-y-1">
                      <li>Intended use cases</li>
                      <li>LinkedIn profile or team page</li>
                      <li>Company website and corporate email domain</li>
                      <li>Organization name, role, and business justification for access</li>
                      <li>Bug bounty platform profiles for researchers (HackerOne, Bugcrowd, etc.)</li>
                      <li>Personal website / portfolio</li>
                    </ul>
                    <p className="mt-2 text-xs opacity-80">
                      Submissions are reviewed by our compliance team. Incomplete requests may delay activation.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="p-5 flex items-center justify-end gap-3 border-t border-neutral-800">
          <button
            onClick={onClose}
            className="h-10 px-4 rounded-md border border-neutral-700 text-neutral-200 hover:bg-white/5"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={loading}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-sky-600/40 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20 disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {loading ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}